import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { OptimizationResult, OptimizationTargetBasis } from "@/types/optimization.types";

export type OptimizationState = {
    targets: Record<number, number>;
    minValues: Record<number, number>;
    maxValues: Record<number, number>;
    selectedIngredients: number[];
    targetBasis: OptimizationTargetBasis;
    result: OptimizationResult | null;
};

const initialState: OptimizationState = {
    targets: {},
    minValues: {},
    maxValues: {},
    selectedIngredients: [],
    targetBasis: "per100g",
    result: null,
};

const optimizationSlice = createSlice({
    name: "optimization",
    initialState,
    reducers: {
        setTargetValue: (state, action: PayloadAction<{ componentId: number; value: number | null }>) => {
            if (action.payload.value === null) {
                delete state.targets[action.payload.componentId];
            } else {
                state.targets[action.payload.componentId] = action.payload.value;
            }
        },
        setMinValue: (state, action: PayloadAction<{ componentId: number; value: number | null }>) => {
            if (action.payload.value === null) {
                delete state.minValues[action.payload.componentId];
            } else {
                state.minValues[action.payload.componentId] = action.payload.value;
            }
        },
        setMaxValue: (state, action: PayloadAction<{ componentId: number; value: number | null }>) => {
            if (action.payload.value === null) {
                delete state.maxValues[action.payload.componentId];
            } else {
                state.maxValues[action.payload.componentId] = action.payload.value;
            }
        },
        setSelectedIngredient: (state, action: PayloadAction<{ ingredientId: number; isSelected: boolean }>) => {
            const wasSelected = state.selectedIngredients.includes(action.payload.ingredientId);
            const isSelected = action.payload.isSelected;

            if (wasSelected && !isSelected) {
                state.selectedIngredients = state.selectedIngredients.filter(
                    (id) => id !== action.payload.ingredientId
                );
            } else if (!wasSelected && isSelected) {
                state.selectedIngredients.push(action.payload.ingredientId);
            }
        },
        setTargetBasis: (state, action: PayloadAction<{ targetBasis: OptimizationTargetBasis }>) => {
            state.targetBasis = action.payload.targetBasis;
        },
        setResult: (state, action: PayloadAction<OptimizationResult>) => {
            state.result = action.payload;
        },
        clearConstraints: (state) => {
            state.targets = {};
            state.minValues = {};
            state.maxValues = {};
        },
        resetOptimization: () => initialState,
    },
});

export const {
    setTargetValue,
    setMinValue,
    setMaxValue,
    setSelectedIngredient,
    setTargetBasis,
    setResult,
    clearConstraints,
    resetOptimization,
} = optimizationSlice.actions;
export const optimizationReducer = optimizationSlice.reducer;
